import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import {
  EdProspectApi,
  EdProspectBulkResult,
  EdProspectSubmission,
  EdProspectSubmissionResponse,
  EdInviteProduct,
  EdSmeInviteResponse,
} from '../tokens/ed-prospect.token';

/**
 * REST implementation of the E-D prospect submission API (BRS §1A.0b, EM-773).
 *
 *   POST /services/embedded/api/v1/ed/prospects
 *   POST /services/embedded/api/v1/ed/prospects/invite?product=   (Idempotency-Key header)
 *   POST /services/embedded/api/v1/ed/prospects/bulk?defaultAction=
 *   GET  /services/embedded/api/v1/ed/prospects/bulk/{jobId}/rejected.xlsx
 *   GET  /services/embedded/api/v1/ed/prospects/bulk/template.xlsx
 *
 * The calling E-D is resolved server-side from the JWT (Rule 22 — no id in the URL).
 * REST-only (Rules 07/10). Bound to ED_PROSPECT in mock-data.providers.ts.
 */
@Injectable({ providedIn: 'root' })
export class EdProspectRestService implements EdProspectApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_PATHS.EMBEDDED_API_BASE}/v1/ed/prospects`;

  submitSingle(request: EdProspectSubmission): Observable<EdProspectSubmissionResponse> {
    return this.http.post<EdProspectSubmissionResponse>(this.base, request);
  }

  submitInvite(
    request: EdProspectSubmission,
    product: EdInviteProduct,
    idempotencyKey: string,
  ): Observable<EdSmeInviteResponse> {
    return this.http.post<EdSmeInviteResponse>(
      `${this.base}/invite?product=${encodeURIComponent(product)}`,
      request,
      { headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) },
    );
  }

  submitBulk(file: File, defaultAction: 'PRE_SCORE' | 'INVITE'): Observable<EdProspectBulkResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<EdProspectBulkResult>(`${this.base}/bulk?defaultAction=${defaultAction}`, form);
  }

  rejectedRowsUrl(jobId: string): string {
    return `${this.base}/bulk/${encodeURIComponent(jobId)}/rejected.xlsx`;
  }

  templateUrl(): string {
    return `${this.base}/bulk/template.xlsx`;
  }
}
